



















































































import { Component, Inject, Watch, Mixins } from "vue-property-decorator";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Components
import BasketItems from "@/components/app/global/baskets/basket/basketView/basketItems.vue";
import BasketControl from "@/components/app/global/baskets/basket/basketView/basketControl.vue";
import ProvisionWarnings from "@/components/app/global/baskets/basket/provision/provisionWarnings.vue";
import CustomFields from "@/components/app/global/customFields/customFields.vue";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IUploadFile } from "@/models/uploadFiles";

@Component({
  components: {
    BasketItems,
    BasketControl,
    CustomFields,
    ProvisionWarnings
  }
})
export default class BasketBasketView extends Mixins(HasValidation, Context) {
  // Inject - Basket provider data
  @Inject({ from: "providerData" })
  public readonly $providerData!: () => IBasketProviderData;

  public get basketData() {
    return this.$providerData();
  }

  // Inject - Basket provider methods
  @Inject({ from: "providerMethods" })
  public readonly $providerMethods!: IBasketProviderMethods;

  public get basketMethods() {
    return this.$providerMethods;
  }

  // Inject Provisioning Provider
  @Inject({ from: "provisionProviderData" })
  public readonly $provisionProviderData!: () => IProvisionProviderData;

  public get provisionData() {
    return this.$provisionProviderData();
  }

  // Inject Provisioning Provider
  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionProviderMethods!: IProvisionProviderMethods;

  public get provisionMethods() {
    return this.$provisionProviderMethods;
  }

  public get basket() {
    return this.basketData.basket;
  }

  public customFieldsDataModule: string = DataModules.CUSTOM_FIELDS_BASKET;

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public customImages: IUploadFile[] = [];
  public basketNote = "";

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      if (this.basketData.basketId === "new") {
        this.basketMethods.goToShop();
        return;
      }
      await this.provisionMethods.checkConfiguration(this.basketData.basketId);
      this.customImages = this.basketData.customImages;
      this.requestError = false;
    } catch (error) {
      if (
        this.$store.getters["api/is403"](error) ||
        this.$store.getters["api/is401"](error)
      ) {
        this.basketMethods.forceBasketReset();
      }
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isReloading = false;
    this.isLoading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async validateBeforeSubmit() {
    try {
      this.isProcessing = true;
      this.basketMethods.setIsProcessing(true);

      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      if (
        !this.isGuest &&
        this.provisionData.hasProvisioning &&
        !this.$_.isEmpty(this.provisionData.provisionErrors)
      ) {
        throw this.$t("_sentence.please_configure_fields_with_warning");
      }

      await this.saveBasketPageData();

      if (this.isGuest) {
        this.isProcessing = false;
        this.basketMethods.setIsProcessing(false);
        return this.basketMethods.goToAuthenticate();
      }

      this.basketMethods.goToCheckout();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.isProcessing = false;
    this.basketMethods.setIsProcessing(false);
  }

  public get hasUnsavedImages() {
    return this.$_.some(
      this.customImages,
      image => !image.hash && !this.$_.isEmpty(image.file)
    );
  }

  public get hasNoProducts() {
    return this.$_.get(this.basket, "products", []).length === 0;
  }

  public async saveBasketPageData() {
    if (this.hasUnsavedImages) {
      // @ts-ignore
      await this.$refs.basketCustomFields.uploadCustomImages(this.isGuest);
      this.customImages = [];
      this.basketMethods.saveCustomImages(this.customImages);
    }

    if (
      !this.$_.isEqual(
        this.basketData.basketForm,
        this.basketData.basketFormClone
      )
    ) {
      await this.basketMethods.updateBasketData(this.basketData.basketForm);
      await this.basketMethods.getBasket(true);
    }
  }

  public goBack() {
    if (this.isProcessing || this.basketData.basketIsProcessing) {
      return;
    }

    this.basketMethods.goToShop();
  }

  public handleImageUploadError(error) {
    if (
      !this.$store.getters["api/is"](error, 422) ||
      !this.$_.get(error, "response.config.url").includes("/image")
    ) {
      return;
    }

    const pathParams = this.$_.get(error, "response.config.url").split("/");
    const fieldId = this.$_.get(pathParams, `[${pathParams.length - 2}]`);

    this.basketMethods.saveCustomImages(
      this.$_.filter(this.customImages, image => image.id !== fieldId)
    );
  }

  @Watch("customImages")
  public onCustomImagesChange() {
    this.basketMethods.saveCustomImages(this.customImages);
  }

  public async beforeDestroy() {
    try {
      await this.saveBasketPageData();
    } catch (error) {
      this.handleImageUploadError(error);
    }
  }
}
